"use client";

import {
  HISTORY_FIELD_LABELS,
  formatHistoryAxisTick,
  historyAxisTicks,
  type HistoryField,
  type ListingFinancialHistory,
} from "@/components/listings/financial-history-model";
import { Switch } from "@/components/ui/switch";
import { prettyNumber } from "@/lib/string-utils";
import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const FIELD_COLOR: Record<HistoryField, string> = {
  askingPrice: "var(--colors-foreground)",
  revenue: "#6366F1",
  profit: "#10B981",
  sde: "#F59E0B",
  ebitda: "#8B5CF6",
};

export function ListingFinancialHistoryChart({
  chart,
}: {
  chart: NonNullable<ListingFinancialHistory["chart"]>;
}) {
  const [activeFields, setActiveFields] = useState<Set<HistoryField>>(
    () => new Set(chart.fields),
  );

  function toggleField(field: HistoryField) {
    setActiveFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  }

  const ticks = historyAxisTicks(chart.points.map((point) => point.timestamp));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {chart.fields.map((field) => {
          const label = HISTORY_FIELD_LABELS[field];
          const color = FIELD_COLOR[field];
          if (chart.fields.length === 1) {
            return (
              <div
                key={field}
                className="inline-flex min-h-11 items-center gap-2 px-1 text-xs font-medium text-foreground"
              >
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
                {label}
              </div>
            );
          }

          return (
            <div
              key={field}
              className="inline-flex min-h-11 items-center gap-2 rounded-md border border-border px-2.5 text-xs font-medium text-foreground"
            >
              <Switch
                checked={activeFields.has(field)}
                onCheckedChange={() => toggleField(field)}
                aria-label={`Show ${label}`}
              />
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden
              />
              {label}
            </div>
          );
        })}
      </div>

      <div className="h-[200px] w-full" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chart.points}
            margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--colors-border)"
              vertical={false}
            />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              ticks={ticks}
              padding={{ left: 16, right: 16 }}
              tickFormatter={(value: number) =>
                formatHistoryAxisTick(value, chart.spanMs)
              }
              tick={{ fontSize: 11, fill: "var(--colors-muted-foreground)" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "var(--colors-muted-foreground)" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(val: number) => `$${prettyNumber(val)}`}
              width={70}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                `$${prettyNumber(value)}`,
                HISTORY_FIELD_LABELS[name as HistoryField] ?? name,
              ]}
              labelFormatter={(label: number) =>
                new Date(label).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              }
              contentStyle={{
                borderRadius: "8px",
                border: "1px solid var(--colors-border)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                fontSize: "12px",
              }}
            />
            {chart.fields.map((field) =>
              activeFields.has(field) ? (
                <Line
                  key={field}
                  type="linear"
                  dataKey={field}
                  stroke={FIELD_COLOR[field]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={false}
                />
              ) : null,
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
